/* A question's comments, for both apps (backend services/questions.py): one thread, oldest first,
   with the answer marked. Posting can answer the question or reopen an answered one; the project
   manager and administrators can mark an earlier comment as the answer. Edit and delete go
   through the project's comments, like any other comment. A cancelled question takes no new
   comments (backend services/questions.py): reopening it opens the discussion again. */

import { useCallback, useEffect, useState } from "react";
import type { Api } from "../api/client";
import { Button, Check, ErrorNote, Loading } from "./basics";
import { Comment, Composer, type Actions, type CommentItem, type CommentPerson } from "./Comments";
import { useLiveReload } from "./live";

const OPEN = ["internal", "client"];
const ANSWERED = ["answered_internal", "answered_client"];

export function QuestionComments({
  api,
  questionId,
  projectId,
  status,
  canMarkAnswer = false,
  onChanged,
  onDraftChange,
}: {
  api: Api;
  questionId: string;
  projectId: string;
  /** The question's status: whether a comment can answer it, or reopen it. */
  status: string;
  /** The project manager and administrators: Mark as answer on earlier comments. */
  canMarkAnswer?: boolean;
  /** The question changed (answered, reopened, new comment): refetch it. */
  onChanged?: () => void;
  /** A comment is typed and not yet posted (or no longer is). */
  onDraftChange?: (drafting: boolean) => void;
}) {
  const [comments, setComments] = useState<CommentItem[] | null>(null);
  const [people, setPeople] = useState<CommentPerson[]>([]);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [flag, setFlag] = useState(false);
  const base = `/projects/${projectId}/comments`;
  const changedRef = useCallback(() => onChanged?.(), [onChanged]);

  const load = useCallback(async () => {
    try {
      const found = await api.get<{ comments: CommentItem[] }>(`/questions/${questionId}/comments`);
      setComments(found.comments);
      setLoadError(null);
    } catch (e) {
      setLoadError(e);
    }
  }, [api, questionId]);

  useEffect(() => {
    void load();
    api
      .get<CommentPerson[]>(`${base}/people`)
      .then(setPeople)
      .catch(() => setPeople([]));
  }, [api, base, load]);
  useLiveReload(load); // someone else commented (UX-187)

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      changedRef();
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (loadError && !comments) return <ErrorNote error={loadError} />;
  if (!comments) return <Loading />;

  const a: Actions = {
    people,
    canSetVisibility: false,
    taskShared: true,
    inTask: true,
    run,
    busy,
    error,
    api,
    base,
  };
  const canAnswer = OPEN.includes(status);
  const canReopen = ANSWERED.includes(status);
  const flagLabel = canAnswer ? "This answers the question" : "Reopen the question";
  // Only an open or answered question can be given an answer: not a cancelled or deferred one.
  const markable = canMarkAnswer && (canAnswer || canReopen);
  return (
    <div className="cm-board" data-live-safe="">
      {comments.length === 0 ? (
        <p className="muted">No comments yet.</p>
      ) : (
        <div className="cm-thread" aria-label="Comments on the question">
          {comments.map((c) => (
            <Comment
              key={c.id}
              comment={c}
              a={a}
              tools={
                markable &&
                !c.is_answer && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      void run(() => api.post(`/questions/${questionId}/answer`, { comment_id: c.id }))
                    }
                  >
                    Mark as answer
                  </Button>
                )
              }
            />
          ))}
        </div>
      )}
      {status === "cancelled" ? (
        <p className="muted">Reopen the question to discuss it.</p>
      ) : (
        <Composer
          people={people}
          placeholder="Comment on the question… (@ to mention)"
          submitLabel="Comment"
          busy={busy}
          error={error}
          onDraftChange={onDraftChange}
          extra={
            (canAnswer || canReopen) && (
              <Check checked={flag} onChange={setFlag}>
                {flagLabel}
              </Check>
            )
          }
          onSubmit={async (body) => {
            const ok = await run(() =>
              api.post(`/questions/${questionId}/comments`, {
                body,
                answered: canAnswer && flag,
                reopen: canReopen && flag,
              }),
            );
            if (ok) setFlag(false);
            return ok;
          }}
        />
      )}
    </div>
  );
}
